import React, { createContext, useContext, useReducer, ReactNode, useCallback } from 'react';
import { subscriptionAPI } from '@services/photographerApi';
import { useAuth } from '@context/AuthContext';
import type {
  CurrentSubscription,
  BillingContextType,
  PlanTier,
  BillingInterval,
  PlanChangePreview,
} from '@/types/photographer';

interface BillingState {
  current: CurrentSubscription | null;
  loading: boolean;
  error: string | null;
}

type BillingAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: CurrentSubscription }
  | { type: 'FETCH_FAILURE'; payload: string };

const initialState: BillingState = { current: null, loading: false, error: null };

function reducer(state: BillingState, action: BillingAction): BillingState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, current: action.payload };
    case 'FETCH_FAILURE':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}

const BillingContext = createContext<BillingContextType | null>(null);

export const BillingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { user } = useAuth();

  const refresh = useCallback(async (): Promise<void> => {
    if (!user?.id) return;
    dispatch({ type: 'FETCH_START' });
    try {
      const current = await subscriptionAPI.getCurrent(user.id);
      dispatch({ type: 'FETCH_SUCCESS', payload: current });
    } catch (error: any) {
      dispatch({ type: 'FETCH_FAILURE', payload: error.message || 'Failed to load billing' });
    }
  }, [user?.id]);

  const fail = (error: any, fallback: string): void => {
    dispatch({ type: 'FETCH_FAILURE', payload: error?.message || fallback });
  };

  const startCheckout = async (plan: PlanTier, interval: BillingInterval): Promise<void> => {
    if (!user?.id) return;
    const origin = window.location.origin;
    try {
      const res = await subscriptionAPI.createCheckout(user.id, {
        plan,
        interval,
        success_url: `${origin}/dashboard/billing?checkout=success`,
        cancel_url: `${origin}/dashboard/billing?checkout=cancel`,
      });
      if (!res.url) {
        fail(null, 'Could not start checkout');
        return;
      }
      window.location.href = res.url;
    } catch (error: any) {
      fail(error, 'Could not start checkout');
    }
  };

  const openPortal = async (): Promise<void> => {
    if (!user?.id) return;
    try {
      const res = await subscriptionAPI.createPortal(
        user.id,
        `${window.location.origin}/dashboard/billing`
      );
      if (!res.url) {
        fail(null, 'Could not open billing portal');
        return;
      }
      window.location.href = res.url;
    } catch (error: any) {
      fail(error, 'Could not open billing portal');
    }
  };

  const cancel = async (): Promise<void> => {
    if (!user?.id) return;
    try {
      await subscriptionAPI.cancel(user.id);
      await refresh();
    } catch (error: any) {
      fail(error, 'Could not cancel subscription');
    }
  };

  const reactivate = async (): Promise<void> => {
    if (!user?.id) return;
    try {
      await subscriptionAPI.reactivate(user.id);
      await refresh();
    } catch (error: any) {
      fail(error, 'Could not reactivate subscription');
    }
  };

  const updateSubscription = async (
    plan: PlanTier,
    interval: BillingInterval
  ): Promise<void> => {
    if (!user?.id) return;
    try {
      await subscriptionAPI.changePlan(user.id, plan, interval);
      await refresh();
    } catch (error: any) {
      fail(error, 'Could not change plan');
    }
  };

  const previewChange = async (
    plan: PlanTier,
    interval: BillingInterval
  ): Promise<PlanChangePreview | null> => {
    if (!user?.id) return null;
    try {
      return await subscriptionAPI.previewChange(user.id, plan, interval);
    } catch (error: any) {
      fail(error, 'Could not preview plan change');
      return null;
    }
  };

  const value: BillingContextType = {
    ...state,
    refresh,
    startCheckout,
    openPortal,
    cancel,
    reactivate,
    updateSubscription,
    previewChange,
  };

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
};

export const useBilling = (): BillingContextType => {
  const context = useContext(BillingContext);
  if (!context) throw new Error('useBilling must be used within BillingProvider');
  return context;
};
